import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useMemo, useRef, useState } from 'react';
import { 
  selectUserDetails, 
  selectUserRoles, 
  selectMappedRoles 
} from '../redux/slices/userSlice';
import { 
  selectComputedPermissions,
  setComputedPermissions,
  fetchUserPermissions,
  selectUserPermissionsStatus,
  selectUserPermissions
} from '../redux/slices/permissionsSlice';
import { selectEnvironment } from '../redux/slices/environmentSlice';
import { useAuth } from '../contexts/AuthContext';
import { permissionEngine } from '../services/permissionEngine';
import { PERMISSION_LEVELS } from '../config/componentRegistry';
import { useGetComponentPoliciesQuery } from '../redux/api/portalApi';

const EMPTY_POLICY_LIST = Object.freeze([]);
const DEFAULT_POLICY_ENVIRONMENTS = Object.freeze(['dev', 'uat', 'production']);

const normalizePolicyEnvironment = (value) => {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'development') return 'dev';
  if (normalized === 'dev' || normalized === 'uat' || normalized === 'production') {
    return normalized;
  }
  return null;
};

const normalizePolicyEnvironments = (policy) => {
  const source = policy?.environments ?? policy?.Environments ?? policy?.environment ?? policy?.Environment;

  if (Array.isArray(source)) {
    const values = source
      .map((value) => normalizePolicyEnvironment(value))
      .filter(Boolean);
    return values.length > 0 ? Array.from(new Set(values)) : [...DEFAULT_POLICY_ENVIRONMENTS];
  }

  if (typeof source === 'string' && source.trim()) {
    const trimmed = source.trim();

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        const values = parsed
          .map((value) => normalizePolicyEnvironment(value))
          .filter(Boolean);
        return values.length > 0 ? Array.from(new Set(values)) : [...DEFAULT_POLICY_ENVIRONMENTS];
      }
    } catch (_) {
      const splitValues = trimmed
        .split(',')
        .map((value) => normalizePolicyEnvironment(value))
        .filter(Boolean);

      if (splitValues.length > 0) {
        return Array.from(new Set(splitValues));
      }
    }

    const normalizedValue = normalizePolicyEnvironment(trimmed);
    if (normalizedValue) {
      return [normalizedValue];
    }
  }

  const normalizedSingle = normalizePolicyEnvironment(source);
  if (normalizedSingle) {
    return [normalizedSingle];
  }

  return [...DEFAULT_POLICY_ENVIRONMENTS];
};

const getPolicyStatus = (policy) => {
  const normalized = String(policy?.status ?? policy?.Status ?? '').trim().toLowerCase();
  return normalized || 'published';
};

const isPublishedPolicy = (policy) => getPolicyStatus(policy) === 'published';

const arePermissionMapsEqual = (left = {}, right = {}) => {
  if (left === right) {
    return true;
  }

  const leftKeys = Object.keys(left || {});
  const rightKeys = Object.keys(right || {});

  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  for (let index = 0; index < leftKeys.length; index += 1) {
    const key = leftKeys[index];
    if (left[key] !== right[key]) {
      return false;
    }
  }

  return true;
};

/**
 * Custom hook for managing component permissions
 * This is the primary interface that components will use to check permissions
 * 
 * Usage:
 * const { canView, canEdit, isLoading } = usePermissions();
 * 
 * // In component render:
 * {canView('home.apps-portal') && <AppsPortalButton />}
 * {canEdit('portal.project-info') ? <EditForm /> : <ReadOnlyForm />}
 */
export const usePermissions = () => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useAuth();
  const [hasComputedPermissionsOnce, setHasComputedPermissionsOnce] = useState(false);
  const lastComputedUserObjectIdRef = useRef(null);
  
  // Get user data from Redux
  const userDetails = useSelector(selectUserDetails);
  const userRoles = useSelector(selectUserRoles);
  const rolesMapped = useSelector(selectMappedRoles);
  const userStatus = useSelector((state) => state.user.status);
  const environment = useSelector(selectEnvironment);
  
  // Get permissions data from Redux
  const computedPermissions = useSelector(selectComputedPermissions);
  const userPermissionsStatus = useSelector(selectUserPermissionsStatus);
  const userPermissions = useSelector(selectUserPermissions);

  const {
    data: componentPoliciesData,
    isLoading: isComponentPoliciesLoading,
    isError: hasComponentPoliciesError,
  } = useGetComponentPoliciesQuery({ status: 'published' }, {
    skip: !isAuthenticated,
  });

  const componentPolicies = componentPoliciesData || EMPTY_POLICY_LIST;

  const runtimePolicies = useMemo(() => {
    if (!Array.isArray(componentPolicies)) {
      return {};
    }

    return componentPolicies.reduce((accumulator, policy) => {
      if (!isPublishedPolicy(policy)) {
        return accumulator;
      }

      const componentKey = policy?.componentKey || policy?.ComponentKey;
      if (!componentKey) {
        return accumulator;
      }

      const environmentKeys = normalizePolicyEnvironments(policy);

      if (!accumulator[componentKey]) {
        accumulator[componentKey] = {};
      }

      environmentKeys.forEach((environmentKey) => {
        const existingPolicy = accumulator[componentKey][environmentKey];
        const existingVersion = Number(existingPolicy?.version ?? existingPolicy?.Version ?? 0);
        const incomingVersion = Number(policy?.version ?? policy?.Version ?? 0);

        if (!existingPolicy || incomingVersion >= existingVersion) {
          accumulator[componentKey][environmentKey] = policy;
        }
      });

      return accumulator;
    }, {});
  }, [componentPolicies]);
  
  const userObjectId = userDetails?.id || userDetails?.localAccountId;

  useEffect(() => {
    if (!isAuthenticated) {
      setHasComputedPermissionsOnce(false);
      lastComputedUserObjectIdRef.current = null;
      return;
    }

    if (lastComputedUserObjectIdRef.current !== userObjectId) {
      setHasComputedPermissionsOnce(false);
      lastComputedUserObjectIdRef.current = userObjectId;
    }
  }, [isAuthenticated, userObjectId]);

  // Fetch user-specific permissions when user changes
  useEffect(() => {
    if (isAuthenticated && userObjectId && userPermissionsStatus === 'idle') {
      dispatch(fetchUserPermissions(userObjectId));
    }
  }, [dispatch, isAuthenticated, userObjectId, userPermissionsStatus]);

  // Compute permissions when dependencies change
  const permissionParams = useMemo(() => ({
    userRoles: userRoles || [],
    rolesMapped: rolesMapped || {},
    userObjectId,
    userPermissions: userPermissions || {},
    runtimePolicies,
    environment: environment || 'Production',
    isAuthenticated: isAuthenticated || false
  }), [userRoles, rolesMapped, userObjectId, userPermissions, runtimePolicies, environment, isAuthenticated]);

  const currentPermissions = useMemo(
    () => permissionEngine.computePermissions(permissionParams),
    [permissionParams]
  );

  // Recompute permissions when params change
  useEffect(() => {
    if (isAuthenticated) {
      const newComputedPermissions = currentPermissions;

      if (!hasComputedPermissionsOnce) {
        setHasComputedPermissionsOnce(true);
      }

      if (!arePermissionMapsEqual(newComputedPermissions, computedPermissions)) {
        dispatch(setComputedPermissions(newComputedPermissions));
      }
    } else {
      if (hasComputedPermissionsOnce) {
        setHasComputedPermissionsOnce(false);
      }

      if (Object.keys(computedPermissions || {}).length > 0) {
        dispatch(setComputedPermissions({}));
      }
    }
  }, [dispatch, currentPermissions, isAuthenticated, computedPermissions, hasComputedPermissionsOnce]);

  // Memoized permission check functions
  const permissionChecks = useMemo(() => {
    return {
      /**
       * Check if user can view a component
       * @param {string} componentKey - Component identifier from registry
       * @returns {boolean} True if user can view the component
       */
      canView: (componentKey) => {
        if (!isAuthenticated || !computedPermissions) return false;
        return permissionEngine.canView(componentKey, computedPermissions);
      },

      /**
       * Check if user can edit a component
       * @param {string} componentKey - Component identifier from registry
       * @returns {boolean} True if user can edit the component
       */
      canEdit: (componentKey) => {
        if (!isAuthenticated || !computedPermissions) return false;
        return permissionEngine.canEdit(componentKey, computedPermissions);
      },

      /**
       * Get the permission level for a component
       * @param {string} componentKey - Component identifier from registry
       * @returns {string} Permission level ('hidden', 'view', 'edit')
       */
      getPermissionLevel: (componentKey) => {
        if (!isAuthenticated || !computedPermissions) return PERMISSION_LEVELS.HIDDEN;
        return permissionEngine.getComponentPermission(componentKey, computedPermissions);
      },

      /**
       * Check if user is admin
       * @returns {boolean} True if user has admin privileges
       */
      isAdmin: () => {
        if (!isAuthenticated) return false;
        return permissionEngine.isAdmin(userRoles || [], rolesMapped || {});
      },

      /**
       * Get user's role level (for admin interfaces)
       * @returns {number} Numeric role level
       */
      getUserRoleLevel: () => {
        if (!isAuthenticated) return 0;
        return permissionEngine.getUserRoleLevel(userRoles || []);
      },

      /**
       * Check multiple permissions at once
       * @param {Array<string>} componentKeys - Array of component identifiers
       * @returns {Object} Object with componentKey: canView boolean pairs
       */
      canViewMultiple: (componentKeys) => {
        const result = {};
        componentKeys.forEach(key => {
          result[key] = permissionChecks.canView(key);
        });
        return result;
      },

      /**
       * Get all permissions for debugging/admin purposes
       * @returns {Object} All computed permissions
       */
      getAllPermissions: () => {
        return computedPermissions || {};
      }
    };
  }, [isAuthenticated, computedPermissions, userRoles, rolesMapped]);

  // Loading state
  const isLoading = useMemo(() => {
    if (!isAuthenticated) {
      return false;
    }

    return (
      userStatus === 'idle' ||
      userStatus === 'loading' ||
      (Boolean(userObjectId) && userPermissionsStatus === 'idle') ||
      userPermissionsStatus === 'loading' ||
      isComponentPoliciesLoading ||
      !arePermissionMapsEqual(currentPermissions, computedPermissions)
    );
  }, [userStatus, userObjectId, userPermissionsStatus, isComponentPoliciesLoading, isAuthenticated, computedPermissions, currentPermissions]);

  // Error state
  const hasError = useMemo(() => {
    return userPermissionsStatus === 'failed' || hasComponentPoliciesError;
  }, [userPermissionsStatus, hasComponentPoliciesError]);

  return {
    ...permissionChecks,
    isLoading,
    hasError,
    userRoles: userRoles || [],
    environment: environment || 'Production'
  };
};

/**
 * Higher-order component for wrapping components with permission checks
 * 
 * Usage:
 * const ProtectedComponent = withPermissions('home.apps-portal')(MyComponent);
 * 
 * @param {string} componentKey - Component identifier from registry
 * @param {Object} options - Options for permission checking
 * @param {boolean} options.requireEdit - Require edit permission instead of view
 * @param {React.Component} options.fallback - Component to render if no permission
 */
export const withPermissions = (componentKey, options = {}) => {
  const { requireEdit = false, fallback: FallbackComponent = null } = options;
  
  return (WrappedComponent) => {
    const PermissionWrappedComponent = (props) => {
      const { canView, canEdit, isLoading } = usePermissions();
      
      if (isLoading) {
        return <div>Loading...</div>; // You can customize this loading state
      }
      
      const hasPermission = requireEdit ? canEdit(componentKey) : canView(componentKey);
      
      if (!hasPermission) {
        return FallbackComponent ? <FallbackComponent {...props} /> : null;
      }
      
      return <WrappedComponent {...props} />;
    };
    
    PermissionWrappedComponent.displayName = `withPermissions(${WrappedComponent.displayName || WrappedComponent.name})`;
    
    return PermissionWrappedComponent;
  };
};
